import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import DateTime24Input from '../components/DateTime24Input';
import { FiCalendar, FiTool, FiLayers, FiX, FiSearch, FiUsers, FiTarget, FiLink, FiPaperclip } from 'react-icons/fi';

const CreateTask = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamTasks, setTeamTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [links, setLinks] = useState([]);
  const [linkInput, setLinkInput] = useState({ url: '', label: '' });
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline: '',
    tools: [],
    technologies: [],
    priority: 'medium',
    status: 'pending',
    team: searchParams.get('team') || '',
    isTeamTask: searchParams.get('isTeamTask') === 'true' || false,
    parentTask: searchParams.get('parentTask') || '',
    assignedTo: '',
  });
  const [toolInput, setToolInput] = useState('');
  const [techInput, setTechInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [techSearchTerm, setTechSearchTerm] = useState('');

  const canManageTeams = user?.role === 'teamLead' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (canManageTeams) fetchTeams();
    if (isAdmin) {
      axios.get('/users').then((res) => setAllUsers(res.data)).catch(() => {});
    }
    if (formData.team && !formData.isTeamTask) {
      fetchTeamTasks();
      fetchTeamMembers();
    }
    if (formData.isTeamTask && formData.team) fetchTeamMembers();
  }, [formData.team, formData.isTeamTask, canManageTeams, isAdmin]);

  const fetchTeams = async () => {
    setLoadingTeams(true);
    try {
      const response = await axios.get('/teams');
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchTeamMembers = async () => {
    if (!formData.team) return;
    try {
      const response = await axios.get(`/teams/${formData.team}`);
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchTeamTasks = async () => {
    if (!formData.team) return;
    try {
      const response = await axios.get('/tasks');
      const tasks = response.data.filter(
        task => task.team?._id === formData.team || task.team === formData.team
      );
      const majorTasks = tasks.filter(task => task.isTeamTask);
      setTeamTasks(majorTasks);
    } catch (error) {
      console.error('Error fetching team tasks:', error);
    }
  };

  const commonTools = ['VS Code', 'Git', 'Docker', 'Postman', 'Figma', 'Slack', 'Jira', 'Trello'];
  
  // Comprehensive technology list organized by category
  const technologiesByCategory = {
    'Frontend Web': [
      'React', 'Vue.js', 'Angular', 'Svelte', 'Next.js', 'Nuxt.js', 'Gatsby', 'Remix',
      'TypeScript', 'JavaScript', 'HTML5', 'CSS3', 'SCSS', 'SASS', 'Tailwind CSS',
      'Bootstrap', 'Material-UI', 'Ant Design', 'Chakra UI', 'Styled Components',
      'Redux', 'Zustand', 'MobX', 'Apollo Client', 'React Query', 'SWR'
    ],
    'Backend Web': [
      'Node.js', 'Express', 'NestJS', 'Fastify', 'Koa', 'Hapi', 'Python', 'Django',
      'Flask', 'FastAPI', 'Ruby on Rails', 'PHP', 'Laravel', 'Symfony', 'CodeIgniter',
      'Java', 'Spring Boot', 'C#', '.NET', 'ASP.NET', 'Go', 'Gin', 'Echo',
      'Rust', 'Actix', 'Rocket', 'Elixir', 'Phoenix', 'Scala', 'Play Framework'
    ],
    'Database': [
      'MongoDB', 'PostgreSQL', 'MySQL', 'SQLite', 'Redis', 'Cassandra', 'Elasticsearch',
      'Firebase', 'Supabase', 'DynamoDB', 'Neo4j', 'CouchDB', 'MariaDB', 'Oracle',
      'InfluxDB', 'TimescaleDB', 'ArangoDB', 'CockroachDB', 'PlanetScale'
    ],
    'Mobile - Android': [
      'Android SDK', 'Java', 'Kotlin', 'Android Studio', 'Jetpack Compose',
      'Material Design', 'Gradle', 'Firebase Android', 'Retrofit', 'OkHttp',
      'Room Database', 'RxJava', 'Coroutines', 'Dagger', 'Hilt', 'Glide'
    ],
    'Mobile - iOS': [
      'Swift', 'Objective-C', 'SwiftUI', 'UIKit', 'Xcode', 'Core Data',
      'Combine', 'Alamofire', 'CocoaPods', 'Swift Package Manager', 'React Native',
      'Flutter', 'Ionic', 'Xamarin'
    ],
    'Cross-Platform Mobile': [
      'React Native', 'Flutter', 'Ionic', 'Xamarin', 'NativeScript', 'Expo',
      'Cordova', 'PhoneGap', 'Unity', 'Unreal Engine', 'Kotlin Multiplatform'
    ],
    'Machine Learning': [
      'Python', 'TensorFlow', 'PyTorch', 'Keras', 'Scikit-learn', 'Pandas',
      'NumPy', 'Matplotlib', 'Jupyter', 'OpenCV', 'NLTK', 'spaCy', 'Hugging Face',
      'XGBoost', 'LightGBM', 'Apache Spark', 'MLflow', 'Weights & Biases',
      'TensorBoard', 'ONNX', 'Caffe', 'Theano', 'CNTK'
    ],
    'Deep Learning & AI': [
      'Neural Networks', 'CNN', 'RNN', 'LSTM', 'Transformer', 'BERT', 'GPT',
      'YOLO', 'GAN', 'Reinforcement Learning', 'Computer Vision', 'NLP',
      'TensorFlow Lite', 'Core ML', 'TensorRT', 'ONNX Runtime'
    ],
    'Game Development': [
      'Unity', 'Unreal Engine', 'Godot', 'Cocos2d', 'Phaser', 'PixiJS',
      'C#', 'C++', 'Lua', 'GameMaker Studio', 'Construct', 'RPG Maker',
      'Blender', 'Maya', '3ds Max', 'ZBrush', 'Substance Painter'
    ],
    'Game Engines & Frameworks': [
      'Unity 3D', 'Unreal Engine 4/5', 'Godot', 'CryEngine', 'Source Engine',
      'Phaser', 'Babylon.js', 'Three.js', 'PixiJS', 'LibGDX', 'SDL',
      'SFML', 'Monogame', 'LÖVE', 'Defold', 'Corona SDK'
    ],
    'Cloud & DevOps': [
      'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Jenkins',
      'GitLab CI', 'GitHub Actions', 'Terraform', 'Ansible', 'Prometheus',
      'Grafana', 'ELK Stack', 'Vercel', 'Netlify', 'Heroku', 'DigitalOcean'
    ],
    'Container & Orchestration': [
      'Docker', 'Kubernetes', 'Docker Compose', 'Podman', 'Rancher',
      'Helm', 'Istio', 'Linkerd', 'OpenShift', 'Nomad', 'Mesos'
    ],
    'Testing': [
      'Jest', 'Mocha', 'Chai', 'Cypress', 'Selenium', 'Playwright',
      'Puppeteer', 'Jasmine', 'Karma', 'Vitest', 'Testing Library',
      'Supertest', 'Postman', 'Newman', 'JMeter', 'K6', 'Artillery'
    ],
    'WebAssembly & Performance': [
      'WebAssembly', 'Rust', 'AssemblyScript', 'WASM', 'Emscripten',
      'Web Workers', 'Service Workers', 'Progressive Web Apps'
    ],
    'Blockchain & Crypto': [
      'Solidity', 'Ethereum', 'Web3', 'Truffle', 'Hardhat', 'IPFS',
      'Hyperledger', 'Bitcoin', 'Chainlink', 'Polygon', 'BSC',
      'Rust', 'Substrate', 'Cosmos', 'Polkadot'
    ],
    'IoT & Embedded': [
      'Arduino', 'Raspberry Pi', 'ESP32', 'MQTT', 'CoAP', 'Embedded C',
      'FreeRTOS', 'Zephyr', 'MicroPython', 'Node-RED', 'Home Assistant'
    ],
    'Desktop Applications': [
      'Electron', 'Tauri', 'Flutter Desktop', 'Qt', 'GTK', 'WPF',
      'JavaFX', 'Swing', 'AWT', 'Windows Forms', 'Cocoa', 'AppKit'
    ],
    'API & GraphQL': [
      'REST API', 'GraphQL', 'Apollo Server', 'Hasura', 'Prisma',
      'gRPC', 'WebSocket', 'Socket.io', 'tRPC', 'JSON-RPC'
    ],
    'Content Management': [
      'WordPress', 'Drupal', 'Joomla', 'Strapi', 'Sanity', 'Contentful',
      'Ghost', 'Gatsby', 'Next.js', 'Headless CMS', 'Prismic'
    ],
    'E-commerce': [
      'Shopify', 'WooCommerce', 'Magento', 'BigCommerce', 'PrestaShop',
      'Stripe', 'PayPal', 'Square', 'Commerce.js', 'Snipcart'
    ],
    'Other Technologies': [
      'Microservices', 'Serverless', 'Lambda', 'Functions', 'Edge Computing',
      'CDN', 'Nginx', 'Apache', 'Load Balancing', 'Message Queue',
      'RabbitMQ', 'Apache Kafka', 'Apache Pulsar', 'Celery', 'Bull'
    ]
  };

  // Flatten all technologies into a single array and remove duplicates
  const commonTechnologies = [...new Set(Object.values(technologiesByCategory).flat())];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

    // Reset related fields when team changes
    if (name === 'team' && !value) {
      setFormData(prev => ({
        ...prev,
        team: '',
        isTeamTask: false,
        parentTask: '',
        assignedTo: '',
      }));
    }

    // Reset parent task when switching to team task
    if (name === 'isTeamTask' && checked) {
      setFormData(prev => ({
        ...prev,
        parentTask: '',
        assignedTo: '',
      }));
    }

    // Reset assignedTo when selecting parent task
    if (name === 'parentTask') {
      if (value) {
        setFormData(prev => ({
          ...prev,
          isTeamTask: false,
          assignedTo: '',
        }));
      }
    }
  };

  const addTool = (tool) => {
    if (tool && !formData.tools.includes(tool)) {
      setFormData({
        ...formData,
        tools: [...formData.tools, tool],
      });
      setToolInput('');
    }
  };

  const removeTool = (tool) => {
    setFormData({
      ...formData,
      tools: formData.tools.filter((t) => t !== tool),
    });
  };

  const addTechnology = (tech) => {
    if (tech && !formData.technologies.includes(tech)) {
      setFormData({
        ...formData,
        technologies: [...formData.technologies, tech],
      });
      setTechInput('');
    }
  };

  const removeTechnology = (tech) => {
    setFormData({
      ...formData,
      technologies: formData.technologies.filter((t) => t !== tech),
    });
  };

  const addLink = () => {
    const url = (linkInput.url || '').trim();
    if (!url) return;
    setLinks((prev) => [...prev, { url, label: (linkInput.label || '').trim() }]);
    setLinkInput({ url: '', label: '' });
  };

  const removeLink = (index) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const onAttachmentChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setAttachmentFiles((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const payload = {
      ...formData,
      team: formData.team || undefined,
      parentTask: formData.parentTask || undefined,
      assignedTo: formData.assignedTo || undefined,
      isTeamTask: formData.isTeamTask || undefined,
      links,
    };

    try {
      let response;
      if (attachmentFiles.length > 0) {
        const formDataToSend = new FormData();
        formDataToSend.append('title', payload.title);
        formDataToSend.append('description', payload.description || '');
        formDataToSend.append('deadline', payload.deadline);
        formDataToSend.append('priority', payload.priority);
        formDataToSend.append('status', payload.status);
        formDataToSend.append('tools', JSON.stringify(payload.tools || []));
        formDataToSend.append('technologies', JSON.stringify(payload.technologies || []));
        formDataToSend.append('links', JSON.stringify(payload.links || []));
        if (payload.team) formDataToSend.append('team', payload.team);
        if (payload.parentTask) formDataToSend.append('parentTask', payload.parentTask);
        if (payload.assignedTo) formDataToSend.append('assignedTo', payload.assignedTo);
        if (payload.isTeamTask) formDataToSend.append('isTeamTask', 'true');
        attachmentFiles.forEach((file) => formDataToSend.append('attachments', file));
        response = await axios.post('/tasks', formDataToSend);
      } else {
        response = await axios.post('/tasks', payload);
      }
      const createdTask = response.data;

      if (formData.parentTask) navigate(`/task/${formData.parentTask}`);
      else if (formData.isTeamTask && formData.team) navigate(`/team/${formData.team}`);
      else navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            {formData.isTeamTask ? 'Team Task' : 
             formData.parentTask ? 'Subtask' : 
             'New Task'}
          </h1>
          <p className="text-dark-100 text-sm">
            {formData.isTeamTask ? 'Major goal with subtasks' :
             formData.parentTask ? 'Subtask for team member' :
             'Add task'}
          </p>
        </div>

        <div className="bg-stroke border border-stroke rounded-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-white mb-2">
                Task Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                placeholder="Enter task title"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-white mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent resize-none"
                placeholder="Enter task description"
              />
            </div>

            {/* Team and Task Type Selection */}
            {canManageTeams && (
              <div className="bg-purple-300/10 border border-purple-300/30 rounded-lg p-4 mb-6">
                <h3 className="text-white font-semibold mb-4 flex items-center">
                  <FiUsers className="mr-2" />
                  Team & Task Configuration
                </h3>
                
                <div className="space-y-4">
                  {/* Team Selection */}
                  <div>
                    <label htmlFor="team" className="block text-sm font-medium text-white mb-2">
                      Team (Optional)
                    </label>
                    <select
                      id="team"
                      name="team"
                      value={formData.team}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                    >
                      <option value="">No Team (Individual Task)</option>
                      {teams.map((team) => (
                        <option key={team._id} value={team._id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Team Task Toggle */}
                  {formData.team && (
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="isTeamTask"
                        name="isTeamTask"
                        checked={formData.isTeamTask}
                        onChange={handleChange}
                        className="w-4 h-4 text-purple-300 rounded focus:ring-purple-300"
                      />
                      <label htmlFor="isTeamTask" className="text-white font-medium flex items-center">
                        <FiTarget className="mr-2" />
                        This is a Team Task (Major Goal)
                      </label>
                    </div>
                  )}

                  {/* Parent Task Selection (for subtasks) */}
                  {formData.team && !formData.isTeamTask && teamTasks.length > 0 && (
                    <div>
                      <label htmlFor="parentTask" className="block text-sm font-medium text-white mb-2">
                        Parent Task (Create as Subtask)
                      </label>
                      <select
                        id="parentTask"
                        name="parentTask"
                        value={formData.parentTask}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                      >
                        <option value="">Create as Independent Task</option>
                        {teamTasks.map((task) => (
                          <option key={task._id} value={task._id}>
                            {task.title}
                          </option>
                        ))}
                      </select>
                      {formData.parentTask && (
                        <p className="text-xs text-purple-200 mt-1">
                          This will be a subtask of the selected team task
                        </p>
                      )}
                    </div>
                  )}

                  {/* Assign to Team Member */}
                  {(formData.team && teamMembers.length > 0 && !formData.isTeamTask) && (
                    <div>
                      <label htmlFor="assignedTo" className="block text-sm font-medium text-white mb-2">
                        Assign to Team Member (Optional)
                      </label>
                      <select
                        id="assignedTo"
                        name="assignedTo"
                        value={formData.assignedTo}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                      >
                        <option value="">Not Assigned</option>
                        {teamMembers.map((member) => {
                          const memberUser = member.user || member;
                          return (
                            <option key={memberUser._id} value={memberUser._id}>
                              {memberUser.name} {member.role && `(${member.role})`}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}

                  {/* Assign to User (admin, no team) */}
                  {isAdmin && !formData.team && allUsers.length > 0 && (
                    <div>
                      <label htmlFor="assignedToUser" className="block text-sm font-medium text-white mb-2">
                        Assign to User (Optional)
                      </label>
                      <select
                        id="assignedToUser"
                        name="assignedTo"
                        value={formData.assignedTo}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                      >
                        <option value="">Not Assigned</option>
                        {allUsers.map((u) => (
                          <option key={u._id} value={u._id}>
                            {u.name} {u.role && `(${u.role})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-white font-semibold flex items-center">
                <FiLink className="mr-2" />
                Links
              </h3>
              <p className="text-dark-100 text-sm">Figma, test domain, or other URLs</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {links.map((link, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 bg-purple-300/20 text-purple-200 px-3 py-1.5 rounded-lg text-sm"
                  >
                    {link.label ? (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {link.label}
                      </a>
                    ) : (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[200px]">
                        {link.url}
                      </a>
                    )}
                    <button type="button" onClick={() => removeLink(i)} className="hover:text-red-400">
                      <FiX size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <input
                  type="url"
                  value={linkInput.url}
                  onChange={(e) => setLinkInput((p) => ({ ...p, url: e.target.value }))}
                  placeholder="URL (e.g. Figma, test domain)"
                  className="flex-1 min-w-[180px] px-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300"
                />
                <input
                  type="text"
                  value={linkInput.label}
                  onChange={(e) => setLinkInput((p) => ({ ...p, label: e.target.value }))}
                  placeholder="Label (optional)"
                  className="w-32 px-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300"
                />
                <button
                  type="button"
                  onClick={addLink}
                  className="px-4 py-2 bg-purple-300 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-white font-semibold flex items-center">
                <FiPaperclip className="mr-2" />
                Attachments
              </h3>
              <p className="text-dark-100 text-sm">Screenshots, docs (PDF, images, etc.)</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {attachmentFiles.map((file, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 bg-dark-300 text-dark-100 px-3 py-1.5 rounded-lg text-sm"
                  >
                    <span className="truncate max-w-[180px]">{file.name}</span>
                    <button type="button" onClick={() => removeAttachment(i)} className="hover:text-red-400">
                      <FiX size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.zip,.txt"
                onChange={onAttachmentChange}
                className="block w-full text-sm text-dark-100 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-300 file:text-white file:font-medium"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="deadline" className="block text-sm font-medium text-white mb-2">
                  <FiCalendar className="inline mr-2 text-white" />
                  Deadline *
                </label>
                <DateTime24Input
                  id="deadline"
                  value={formData.deadline}
                  onChange={(v) => setFormData((prev) => ({ ...prev, deadline: v }))}
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-white mb-2">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-background border border-dark-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                <FiTool className="inline mr-2" />
                Tools
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.tools.map((tool) => (
                  <span
                    key={tool}
                    className="inline-flex items-center space-x-1 bg-purple-300/20 text-purple-200 px-3 py-1 rounded-lg text-sm"
                  >
                    <span>{tool}</span>
                    <button
                      type="button"
                      onClick={() => removeTool(tool)}
                      className="hover:text-red-400"
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={toolInput}
                  onChange={(e) => setToolInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTool(toolInput.trim());
                    }
                  }}
                  placeholder="Add a tool"
                  className="flex-1 px-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => addTool(toolInput.trim())}
                  className="px-4 py-2 bg-purple-300 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {commonTools.filter(tool => !formData.tools.includes(tool)).map((tool) => (
                  <button
                    key={tool}
                    type="button"
                    onClick={() => addTool(tool)}
                    className="px-3 py-1 bg-dark-300 text-dark-100 rounded-lg text-sm hover:bg-purple-300/20 hover:text-purple-200 transition-colors"
                  >
                    {tool}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                <FiLayers className="inline mr-2" />
                Technologies
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.technologies.map((tech) => (
                  <span
                    key={tech}
                    className="inline-flex items-center space-x-1 bg-purple-200/20 text-purple-100 px-3 py-1 rounded-lg text-sm"
                  >
                    <span>{tech}</span>
                    <button
                      type="button"
                      onClick={() => removeTechnology(tech)}
                      className="hover:text-red-400"
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={techInput}
                  onChange={(e) => setTechInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTechnology(techInput.trim());
                    }
                  }}
                  placeholder="Add a technology"
                  className="flex-1 px-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => addTechnology(techInput.trim())}
                  className="px-4 py-2 bg-purple-300 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  Add
                </button>
              </div>
              {/* Category Filter */}
              <div className="mb-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategory('All');
                      setTechSearchTerm('');
                    }}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === 'All'
                        ? 'bg-purple-300 text-white'
                        : 'bg-dark-300 text-dark-100 hover:bg-purple-300/20 hover:text-purple-200'
                    }`}
                  >
                    All
                  </button>
                  {Object.keys(technologiesByCategory).map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => {
                        setSelectedCategory(category);
                        setTechSearchTerm('');
                      }}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === category
                          ? 'bg-purple-300 text-white'
                          : 'bg-dark-300 text-dark-100 hover:bg-purple-300/20 hover:text-purple-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                
                {/* Search Input */}
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-100" size={16} />
                  <input
                    type="text"
                    value={techSearchTerm}
                    onChange={(e) => setTechSearchTerm(e.target.value)}
                    placeholder="Search technologies..."
                    className="w-full pl-10 pr-4 py-2 bg-background border border-dark-300 rounded-lg text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Technologies List */}
              <div className="max-h-64 overflow-y-auto border border-dark-300 rounded-lg p-4 bg-background/50">
                {(selectedCategory === 'All' 
                  ? commonTechnologies 
                  : technologiesByCategory[selectedCategory] || []
                )
                  .filter(tech => 
                    !formData.technologies.includes(tech) &&
                    tech.toLowerCase().includes(techSearchTerm.toLowerCase())
                  )
                  .map((tech) => (
                    <button
                      key={tech}
                      type="button"
                      onClick={() => {
                        addTechnology(tech);
                        setTechSearchTerm('');
                      }}
                      className="px-3 py-1.5 bg-dark-300 text-dark-100 rounded-lg text-sm hover:bg-purple-300/20 hover:text-purple-200 transition-colors mr-2 mb-2"
                    >
                      {tech}
                    </button>
                  ))}
                
                {((selectedCategory === 'All' 
                  ? commonTechnologies 
                  : technologiesByCategory[selectedCategory] || []
                ).filter(tech => 
                  !formData.technologies.includes(tech) &&
                  tech.toLowerCase().includes(techSearchTerm.toLowerCase())
                ).length === 0) && (
                  <p className="text-dark-100 text-sm text-center py-4">
                    {techSearchTerm ? 'No technologies found matching your search.' : 'All technologies in this category are selected.'}
                  </p>
                )}
              </div>
              
              {/* Technology Count Info */}
              <p className="text-xs text-dark-100 mt-2">
                {commonTechnologies.filter(tech => !formData.technologies.includes(tech)).length} technologies available
              </p>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 px-6 py-3 bg-dark-300 text-white rounded-lg font-semibold hover:bg-dark-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-200 to-purple-300 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTask;

